import { useMemo, useState } from 'react';

import { useGetIncubatorTypesQuery, useGetLabelsQuery } from '@/app/services/fertWork';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import {
  EVEN_PREFERENCE,
  getStoredIncubatorSelection,
  setStoredIncubatorSelection,
} from '../utils/incubatorSelection';
import { validateIncubatorPlan } from '../utils/incubatorPlanValidation';

export const useIncubatorSelection = (labelCount: number, sessionId?: string) => {
  const { data: incubatorTypes = [] } = useGetIncubatorTypesQuery();
  const { data: labelsData } = useGetLabelsQuery(sessionId || '', {
    skip: !sessionId,
  });
  const stored = useMemo(() => getStoredIncubatorSelection(), []);
  const [counts, setCounts] = useState<Record<string, number>>(
    stored.incubatorCounts,
  );
  const [order, setOrder] = useState<string[]>(stored.preferenceOrder);
  const heracell25Slots = useFeatureFlag('fert_work_heracell_25_slots');

  const capacityFor = (name: string, catalogCapacity: number) =>
    name === 'HeraCell' && !heracell25Slots ? 16 : catalogCapacity;

  const activeTypeNames = useMemo(
    () =>
      incubatorTypes
        .filter((it) => (counts[it.name] || 0) > 0)
        .map((it) => it.name),
    [incubatorTypes, counts],
  );

  const isEven = order.length === 1 && order[0] === EVEN_PREFERENCE;

  const orderedActiveTypes = useMemo(() => {
    const known = order.filter(
      (name) => name !== EVEN_PREFERENCE && activeTypeNames.includes(name),
    );
    const missing = activeTypeNames.filter((name) => !known.includes(name));
    return [...known, ...missing];
  }, [order, activeTypeNames]);

  const totalCapacity = incubatorTypes.reduce(
    (sum, it) =>
      sum + (counts[it.name] || 0) * capacityFor(it.name, it.capacity),
    0,
  );

  // Full validation (mirrors the API's PlanIncubatorMaps): catches "not enough space" as well
  // as the stricter "not enough <type> incubators" case where total capacity looks sufficient
  // but a category/species combination can't be packed into the available units of a type.
  const planValidationError = useMemo(
    () =>
      validateIncubatorPlan(
        labelsData ?? [],
        incubatorTypes,
        counts,
        isEven ? [EVEN_PREFERENCE] : orderedActiveTypes,
        capacityFor,
      ).error,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labelsData, incubatorTypes, counts, isEven, orderedActiveTypes],
  );

  const hasCapacityError =
    activeTypeNames.length > 0 &&
    (labelCount > totalCapacity || planValidationError != null);
  const preferenceDisabled = activeTypeNames.length <= 1;

  const setCount = (name: string, value: number) => {
    setCounts((prev) => ({ ...prev, [name]: Math.max(0, value) }));
  };

  const moveType = (name: string, direction: -1 | 1) => {
    setOrder((prev) => {
      const current = orderedActiveTypes;
      const index = current.indexOf(name);
      const swapWith = index + direction;
      if (swapWith < 0 || swapWith >= current.length) return prev;
      const next = [...current];
      [next[index], next[swapWith]] = [next[swapWith], next[index]];
      return next;
    });
  };

  const toggleEven = () => {
    setOrder(isEven ? orderedActiveTypes : [EVEN_PREFERENCE]);
  };

  const buildSelection = () => {
    const incubatorCounts = Object.fromEntries(
      activeTypeNames.map((name) => [name, counts[name]]),
    );
    const preferenceOrder = isEven ? [EVEN_PREFERENCE] : orderedActiveTypes;
    setStoredIncubatorSelection({ incubatorCounts, preferenceOrder });
    return { incubatorCounts, preferenceOrder };
  };

  return {
    incubatorTypes,
    counts,
    setCount,
    activeTypeNames,
    orderedActiveTypes,
    isEven,
    toggleEven,
    moveType,
    preferenceDisabled,
    totalCapacity,
    hasCapacityError,
    planValidationError,
    buildSelection,
  };
};
